'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const LeadCaptureSection = () => {
  const [email, setEmail] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    // Simulate submission — can be wired to Supabase/Resend later
    await new Promise((r) => setTimeout(r, 800));
    setSubmitted(true);
    setLoading(false);
  };

  const resources = [
    { icon: 'DocumentArrowDownIcon', title: 'Hydraulic Jack Installation Guide', type: 'PDF Guide' },
    { icon: 'DocumentArrowDownIcon', title: 'Elevator Wiring Schematic Checklist', type: 'Checklist' },
    { icon: 'DocumentArrowDownIcon', title: 'Troubleshooting: Common Drive Faults', type: 'PDF Guide' },
    { icon: 'DocumentArrowDownIcon', title: 'State Code Compliance Reference', type: 'Reference Sheet' },
  ];

  return (
    <section className="py-16 lg:py-24 bg-black text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Left: Resource List */}
          <div>
            <div className="inline-flex items-center gap-2 bg-red-600 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 mb-6">
              Resource Center
            </div>
            <h2 className="font-bold text-3xl lg:text-4xl mb-4 leading-tight">
              Free Technical Guides for Industry Professionals
            </h2>
            <p className="text-gray-400 mb-8 leading-relaxed">
              Get instant access to installation guides, troubleshooting checklists, and code compliance references — built for elevator technicians and contractors.
            </p>
            <ul className="space-y-4">
              {resources.map((resource) => (
                <li key={resource.title} className="flex items-center gap-3 group">
                  <div className="flex-shrink-0 bg-red-600/20 border border-red-600/30 p-2">
                    <Icon name={resource.icon as any} size={18} className="text-red-400" />
                  </div>
                  <div>
                    <p className="font-medium text-white text-sm">{resource.title}</p>
                    <p className="text-xs text-gray-500 uppercase tracking-wide">{resource.type}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>

          {/* Right: Lead Capture Form */}
          <div className="bg-gray-900 border border-gray-800 p-8">
            {submitted ? (
              <div className="text-center py-8">
                <div className="inline-flex items-center justify-center bg-red-600 rounded-full p-4 mb-4">
                  <Icon name="CheckIcon" size={28} className="text-white" />
                </div>
                <h3 className="font-bold text-xl mb-2">You&apos;re In!</h3>
                <p className="text-gray-400 text-sm">
                  Check your inbox — your technical guides are on the way. Visit our full{' '}
                  <a href="/resources" className="text-red-400 hover:text-red-300 underline">
                    Resources Center
                  </a>{' '}
                  for more.
                </p>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-3 mb-6">
                  <div className="bg-red-600 p-2">
                    <Icon name="EnvelopeIcon" size={20} className="text-white" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg">Get Instant Access</h3>
                    <p className="text-gray-400 text-xs">Enter your email to download all guides free</p>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="lead-email" className="block text-xs font-semibold uppercase tracking-wide text-gray-400 mb-2">
                      Work Email Address
                    </label>
                    <input
                      id="lead-email"
                      type="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="you@company.com"
                      className="w-full bg-black border border-gray-700 text-white px-4 py-3 text-sm focus:outline-none focus:border-red-600 transition-colors placeholder-gray-600"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-red-600 text-white py-3 font-bold text-sm tracking-wide hover:bg-red-700 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
                  >
                    {loading ? (
                      <>
                        <Icon name="ArrowPathIcon" size={16} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Icon name="DocumentArrowDownIcon" size={16} />
                        Download Free Guides
                      </>
                    )}
                  </button>
                  <p className="text-xs text-gray-600 text-center">
                    No spam. Unsubscribe anytime. For industry professionals only.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default LeadCaptureSection;
